import { action, computed, makeObservable, observable } from "mobx";
import { IStudentSessionReportViewModel } from "../Interfaces/IStudentSessionReportViewModel";
import { StudentTargetViewModel } from "Views/Shared/Views/StudentTarget/StudentTargetViewModel";
import { StudentSessionAQACertificatesViewModel } from "Views/Shared/Views/StudentSessionAQACertificate/StudentSessionAQACertificatesViewModel";
import { StudentSessionLegacyFormViewModel } from "Views/Shared/Views/StudentSessionLegacyForm/StudentSessionLegacyFormViewModel";
import { StudentAbsenceViewModel } from "../../../../Student/StudentReports/StudentAbsence/StudentAbsenceViewModel";
import { StudentBehaviourViewModel } from "Views/Shared/Views/StudentBehaviour/StudentBehaviourViewModel";
import { FilesViewModel } from "../../../../Student/StudentReports/Files";

type ExportButtonDisplayNameType = "Export Report" | "Exporting";
type UpsertButtonDisplayNameType = "Update" | "Updating" | "Create" | "Creating";

/**
 * Manages observable state and UI lifecycle for SessionReportViewModel.
 * Responsibilities: observable properties, computed properties, state mutations.
 */
export class MobXManager {
    // Observable State
    public _showSessionDocPhoto: boolean = false;
    public _isEditForm: boolean = false;
    public _formErrorMessage: string | null = null;
    public _cancelUpdateWarningMessage: string | null = null;
    public _showOtherAbsenceText: boolean = false;
    public _isUpserting: boolean = false;
    public _isExporting: boolean = false;
    public _exportButtonDisplayName: ExportButtonDisplayNameType = "Export Report";
    public _upsertButtonDisplayName: UpsertButtonDisplayNameType = "Create";

    // Observable ViewModels
    public _studentTargetViewModels = observable<StudentTargetViewModel>([]);
    public _studentBehaviourViewModels = observable<StudentBehaviourViewModel>([]);
    public _filesViewModel = new FilesViewModel();
    public _studentSessionAQACertificatesViewModel!: StudentSessionAQACertificatesViewModel;
    public _studentSessionLegacyFormViewModel?: StudentSessionLegacyFormViewModel;
    public _studentAbsenceViewModel!: StudentAbsenceViewModel;

    constructor(private viewModel: IStudentSessionReportViewModel) {
        makeObservable(this, {
            // Observables - UI State
            _showSessionDocPhoto: observable,
            _isEditForm: observable,
            _formErrorMessage: observable,
            _cancelUpdateWarningMessage: observable,
            _showOtherAbsenceText: observable,
            _isUpserting: observable,
            _isExporting: observable,
            _exportButtonDisplayName: observable,
            _upsertButtonDisplayName: observable,

            // Observables - ViewModels
            _studentTargetViewModels: observable,
            _studentBehaviourViewModels: observable,
            _filesViewModel: observable,
            _studentSessionAQACertificatesViewModel: observable,
            _studentSessionLegacyFormViewModel: observable,
            _studentAbsenceViewModel: observable,

            // Computed
            showSessionDocPhoto: computed,
            isEditForm: computed,
            formErrorMessage: computed,
            cancelUpdateWarningMessage: computed,
            showOtherAbsenceText: computed,
            isUpserting: computed,
            isExporting: computed,
            exportButtonDisplayName: computed,
            upsertButtonDisplayName: computed,
            canSubmitSessionDoc: computed,
            showFormErrorWarning: computed,
            showCancelUpdateWarningMessage: computed,
            studentTargetViewModels: computed,
            studentBehaviourViewModels: computed,
            filesViewModel: computed,
            studentSessionAQACertificatesViewModel: computed,
            studentSessionLegacyFormViewModel: computed,
            studentAbsenceViewModel: computed,

            // Actions - UI State
            setShowSessionDocPhoto: action,
            setIsEditForm: action,
            setFormErrorMessage: action,
            setCancelUpdateWarningMessage: action,
            setShowOtherAbsenceText: action,
            setIsUpserting: action,
            setIsExporting: action,
            setExportButtonDisplayName: action,
            setUpsertButtonDisplayName: action,
            setCanSubmitSessionDoc: action,
            toggleShowSessionDocPhoto: action,
            resetUIState: action,

            // Actions - ViewModels
            setStudentTargetViewModels: action,
            setStudentBehaviourViewModels: action,
            setSessionAQACertificatesViewModel: action,
            setLegacyFormViewModel: action,
            resetFilesViewModel: action,
            setStudentAbsenceViewModel: action,
        });

        this.initializeViewModels();
    }

    private initializeViewModels(): void {
        this._studentAbsenceViewModel = new StudentAbsenceViewModel(this.viewModel.model.studentAbsence);
        this._studentSessionAQACertificatesViewModel = new StudentSessionAQACertificatesViewModel(this.viewModel.model.studentSessionAQACertificatesModel);
        this.viewModel.model.filesViewModel = this._filesViewModel;
    }

    // Computed Properties
    public get showSessionDocPhoto(): boolean {
        return this._showSessionDocPhoto;
    }

    public get isEditForm(): boolean {
        return this._isEditForm;
    }

    public get formErrorMessage(): string | null {
        return this._formErrorMessage;
    }

    public get cancelUpdateWarningMessage(): string | null {
        return this._cancelUpdateWarningMessage;
    }

    public get showOtherAbsenceText(): boolean {
        return this._showOtherAbsenceText;
    }

    public get isUpserting(): boolean {
        return this._isUpserting;
    }

    public get isExporting(): boolean {
        return this._isExporting;
    }

    public get exportButtonDisplayName(): ExportButtonDisplayNameType {
        return this._exportButtonDisplayName;
    }

    public get upsertButtonDisplayName(): UpsertButtonDisplayNameType {
        return this._upsertButtonDisplayName;
    }

    public get canSubmitSessionDoc(): boolean {
        // this.viewModel.isFieldValid("documentCategoryId");
        const error = this.viewModel.getError("documentCategoryId");
        return error === undefined;
    }

    public get showFormErrorWarning(): boolean {
        return this._formErrorMessage !== null;
    }

    public get showCancelUpdateWarningMessage(): boolean {
        return this._cancelUpdateWarningMessage !== null;
    }

    // ViewModel Getters
    public get studentTargetViewModels(): StudentTargetViewModel[] {
        return this._studentTargetViewModels;
    }

    public get studentBehaviourViewModels(): StudentBehaviourViewModel[] {
        return this._studentBehaviourViewModels;
    }

    public get filesViewModel(): FilesViewModel {
        return this._filesViewModel;
    }

    public get studentSessionAQACertificatesViewModel(): StudentSessionAQACertificatesViewModel {
        return this._studentSessionAQACertificatesViewModel;
    }

    public get studentSessionLegacyFormViewModel(): StudentSessionLegacyFormViewModel | undefined {
        return this._studentSessionLegacyFormViewModel;
    }

    public get studentAbsenceViewModel(): StudentAbsenceViewModel {
        return this._studentAbsenceViewModel;
    }

    // State Setters (Actions)
    public setShowSessionDocPhoto(value: boolean): void {
        this._showSessionDocPhoto = value;
    }

    public setIsEditForm(value: boolean): void {
        this._isEditForm = value;
    }

    public setFormErrorMessage(value: string | null): void {
        this._formErrorMessage = value;
    }

    public setCancelUpdateWarningMessage(value: string | null): void {
        this._cancelUpdateWarningMessage = value;
    }

    public setShowOtherAbsenceText(value: boolean): void {
        this._showOtherAbsenceText = value;
    }

    public setIsUpserting(value: boolean): void {
        this._isUpserting = value;
    }

    public setIsExporting(value: boolean): void {
        this._isExporting = value;
    }

    public setExportButtonDisplayName(value: ExportButtonDisplayNameType): void {
        this._exportButtonDisplayName = value;
    }

    public setUpsertButtonDisplayName(value: UpsertButtonDisplayNameType): void {
        this._upsertButtonDisplayName = value;
    }

    public setCanSubmitSessionDoc(value: boolean): void {
        // This is computed, so we don't set it directly
    }

    public toggleShowSessionDocPhoto(): void {
        this._showSessionDocPhoto = !this._showSessionDocPhoto;
        if (this._showSessionDocPhoto) {
            this.viewModel.filesViewModel.model.files.clear();
            this.viewModel.setValue("documentCategoryId", "");
            this.viewModel.setValid("documentCategoryId", false);
        }
    }

    public resetUIState(): void {
        this.setShowSessionDocPhoto(false);
        this.setFormErrorMessage(null);
        this.setCancelUpdateWarningMessage(null);
        this.setShowOtherAbsenceText(false);
        this.setIsUpserting(false);
        this.setIsExporting(false);
    }

    // ViewModel Actions
    public setStudentTargetViewModels(viewModels: StudentTargetViewModel[]): void {
        this._studentTargetViewModels.replace(viewModels);
    }

    public setStudentBehaviourViewModels(viewModels: StudentBehaviourViewModel[]): void {
        this._studentBehaviourViewModels.replace(viewModels);
    }

    public setSessionAQACertificatesViewModel(viewModel: StudentSessionAQACertificatesViewModel): void {
        this._studentSessionAQACertificatesViewModel = viewModel;
    }

    public setLegacyFormViewModel(viewModel: StudentSessionLegacyFormViewModel): void {
        this._studentSessionLegacyFormViewModel = viewModel;
    }

    public resetFilesViewModel(): void {
        this._filesViewModel = new FilesViewModel();
    }

    public setStudentAbsenceViewModel(viewModel: StudentAbsenceViewModel): void {
        this._studentAbsenceViewModel = viewModel;
    }
}
